/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TenantService } from '@src/app/shared/tenant.service';
import { MessageService } from '@src/app/shared/message.service';
import { Unit } from '@src/app/model/unit';
import { Omc } from '@src/app/model/Omc';
import { catchError, first, map, shareReplay, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { HttpErrorHandler } from '@src/app/shared/HttpErrorHandler';
import { Person } from '@src/app/model/person';
import { TenantId, uuid } from '@src/app/model/abstract-object';
import { BCode } from '@src/app/model/bcode';
import { Schedule } from '@src/app/model/schedule';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';

@Injectable({
    providedIn: 'root'
})
export class AgentDataService {
    protected http = inject(HttpClient);
    protected messageService = inject(MessageService);
    private currentUserSvc = inject(CurrentUserService);


    protected omcUnitCacheMap = new Map<uuid, Unit[]>();
    protected omcPersonCacheMap = new Map<uuid, Person[]>();
    protected omcExpenseBCodeCacheMap = new Map<uuid, BCode[]>();
    protected omcScheduleCacheMap = new Map<uuid, Schedule[]>();

    protected omcListCacheMap = new Map<uuid, Observable<Omc[]>>();

    private errorHandler = new HttpErrorHandler();

    /** The agency's OMCs without the OMC page's aggregates - names, addresses and officers only. */
    getOmcList(): Observable<Omc[]> {
        const agentTeamId = this.currentUserSvc.currentUser?.inAgency?.teamId;
        if (!agentTeamId) {
            return of([]);
        }
        if (!this.omcListCacheMap.has(agentTeamId)) {
            // shareReplay holds the one response for everyone who asks later; first() is what makes
            // this complete like the http call it replaces, so no subscriber has to unsubscribe.
            this.omcListCacheMap.set(agentTeamId,
                this.http.get<Omc[]>(TenantService.ajaxPath + 'agent/omcList').pipe(
                    catchError((error) => {
                        this.omcListCacheMap.delete(agentTeamId);
                        this.messageService.show(this.errorHandler.getErrorMessage(error, 'OMC List'));
                        return of([] as Omc[]);
                    }),
                    shareReplay({ bufferSize: 1, refCount: false }),
                    first()
                ));
        }
        return this.omcListCacheMap.get(agentTeamId);
    }

    clearOmcList() {
        this.omcListCacheMap.clear();
    }

    /**
     * One OMC, with the officers and nominated request owner needed to default a request's owner.
     *
     * From the agency's cached list where there is one; inside an OMC there is no list to cache, so
     * the single row is fetched. Either way the server answers through the OMC team's primary
     * agent, which is the agency the work will actually be filed against.
     */
    getOmcFor(omcTeamId: TenantId): Observable<Omc> {
        const user = this.currentUserSvc.currentUser;
        const cached = this.omcListCacheMap.get(user?.inAgency?.teamId ?? user?.fromAgentTeamId);
        if (!cached) {
            return this.fetchOmcFor(omcTeamId);
        }
        // A cached list that does not hold this OMC is not an answer - an agent reaching one they
        // did not arrive through still needs asking for.
        return cached.pipe(switchMap(omcs => {
            const omc = omcs.find(o => o.omcTeamId === omcTeamId);
            return omc ? of(omc) : this.fetchOmcFor(omcTeamId);
        }));
    }

    private fetchOmcFor(omcTeamId: TenantId): Observable<Omc> {
        return this.http.get<Omc>(TenantService.ajaxPath + 'agent/omcFor/' + omcTeamId).pipe(
            catchError(error => {
                this.messageService.show(this.errorHandler.getErrorMessage(error, 'Managing Agency'));
                return of(null as Omc);
            })
        );
    }

    /*
        getOmcUnit(omc: Omc, unitId: uuid) : Observable<Unit>{
            if (this.omcUnitCacheMap.has(omc.omcTeamId)) {
                return of(this.omcUnitCacheMap.get(omc.omcTeamId).find( o => o.id === unitId));
            } else {
                return this.getOmcUnits(omc).pipe( map( (data: Unit[] ) => {
                    return data.find( o => o.id === unitId);
                }));
            }
        }
    */
    getOmcUnits(omc: Omc): Observable<Unit[]> {
        const url = TenantService.ajaxPath + 'units';

        if (this.omcUnitCacheMap.has(omc.omcTeamId)) {
            return of(this.omcUnitCacheMap.get(omc.omcTeamId));
        } else {
            return this.http.get(url, { params: { _forceTeam: omc.omcTeamId } }).pipe(
                map((data: Unit[]) => {
                    this.omcUnitCacheMap.set(omc.omcTeamId, data);
                    return data;
                }),
                catchError((error) => {
                    this.messageService.show(this.errorHandler.getErrorMessage(error, 'OMC Units for ' + omc.name));
                    return of([]);
                })
            );
        }
    }

    getOmcExpenseBCodes(omcTeamId): Observable<BCode[]> {
        const url = TenantService.ajaxPath + 'budgets/bcodes';

        if (this.omcExpenseBCodeCacheMap.has(omcTeamId)) {
            return of(this.omcExpenseBCodeCacheMap.get(omcTeamId));
        } else {
            return this.http.get(url, { params: { typeId: BCode.TYPE.EXPENSE.id,_forceTeam: omcTeamId } }).pipe(
                map((data: BCode[]) => {
                    this.omcExpenseBCodeCacheMap.set(omcTeamId, data);
                    return data;
                }),
                catchError((error) => {
                    this.messageService.show(this.errorHandler.getErrorMessage(error, 'OMC Expense BCodes for ' + omcTeamId));
                    return of([]);
                })
            );
        }
    }

    getOmcSchedules(omcTeamId: uuid): Observable<Schedule[]> {
        const url = TenantService.ajaxPath + 'budgets/schedules';

        if (this.omcScheduleCacheMap.has(omcTeamId)) {
            return of(this.omcScheduleCacheMap.get(omcTeamId));
        } else {
            return this.http.get(url, { params: { _forceTeam: omcTeamId } }).pipe(
                map((data: Schedule[]) => {
                    this.omcScheduleCacheMap.set(omcTeamId, data);
                    return data;
                }),
                catchError((error) => {
                    this.messageService.show(this.errorHandler.getErrorMessage(error, 'OMC Schedules for ' + omcTeamId));
                    return of([]);
                })
            );
        }
    }


    updatePerson(person: Person) {
        const people = this.omcPersonCacheMap.get(person.teamId)
        if (people) {
            const idx = people.findIndex(o => o.id === person.id);
            if (idx >= 0) {
                people.splice(idx, 1);
            }
            people.unshift(person);
        }
    }

    getOmcPeople(omc: Omc): Observable<Person[]> {
        const url = TenantService.ajaxPath + 'people';

        if (this.omcPersonCacheMap.has(omc.omcTeamId)) {
            return of(this.omcPersonCacheMap.get(omc.omcTeamId));
        } else {
            return this.http.get(url, { params: { _forceTeam: omc.omcTeamId } }).pipe(
                map((data: Person[]) => {
                    this.omcPersonCacheMap.set(omc.omcTeamId, data);
                    return data;
                }),
                catchError((error) => {
                    this.messageService.show(this.errorHandler.getErrorMessage(error, 'OMC People for ' + omc.name));
                    return of([]);
                })
            );
        }
    }
}
